<template>
  <div id="scroll-threshold-example" class="coupon-page">
    <div>
      <div class="mt-5" v-if="settings && settings.options">
        <v-row :class="$vuetify.breakpoint.smAndDown ? '' : 'mx-1'">
          <v-col cols="12">
            <v-container style="max-width:640px">
              <v-row class="px-3">
                <!-- Brand / Header Card -->
                <v-col cols="12" md="12" class="pb-2">
                  <v-card class="brand-card text-center" flat>
                    <div class="brand-card-inner justify-center py-4">
                      <v-img
                        v-if="settings.options.logoDB"
                        :src="'/static/users/pictures/db/' + settings.options.logoDB"
                        :lazy-src="'/static/users/pictures/db/' + settings.options.logoDB"
                        max-width="220"
                        class="brand-logo mx-auto"
                      ></v-img>
                      <p v-else class="brand-name text-h5 mb-0">{{ settings.options.brand || 'Coupon Omaggio' }}</p>
                    </div>
                  </v-card>
                  <div class="greeting-block text-center mt-3">
                    <span class="guest-name text-h5">Coupon Omaggio</span>
                  </div>
                </v-col>

                <!-- Loading State -->
                <v-col cols="12" v-if="loading && !settings">
                  <v-card class="text-center brand-card py-8" flat>
                    <v-progress-circular indeterminate size="64" width="3" color="#000000"></v-progress-circular>
                  </v-card>
                </v-col>

                <template v-else>
                  <v-col cols="12" class="pt-0">
                    
                    <!-- Coupon Image & Text Card -->
                    <v-card class="coupon-card mb-4" flat v-if="settings.picture || settings.couponText1">
                      <div class="py-2" v-if="settings.picture">
                        <v-img
                          :src="'/static/users/pictures/coupons/' + settings.picture"
                          :lazy-src="'/static/users/pictures/coupons/' + settings.picture"
                          max-height="280"
                          contain
                          class="mx-auto rounded"
                        ></v-img>
                      </div>
                      <div class="coupon-text text-body-1 text-center mt-3" v-html="settings.couponText1"></div>
                    </v-card>

                    <!-- Validity & Availability Section -->
                    <div class="coupon-section">
                      <div class="validity-item text-center mb-3">
                        <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
                          Valido fino al
                        </div>
                        <div :class="settings.expired ? 'red--text text--darken-2 text-h4 font-weight-bold' : 'emerald-text text-h4 font-weight-bold'">
                          {{ settings.expiry }}
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <div class="availability-box text-center py-2">
                        <div class="text-subtitle-2 mb-1">Utilizzabile oggi:</div>
                        <v-chip
                          v-if="settings.validDoW.includes(localizedDoW) && !settings.spentDate"
                          color="#00AF87"
                          dark
                          label
                          class="font-weight-bold px-4"
                        >
                          SÌ
                        </v-chip>
                        <v-chip
                          v-else
                          color="#D32F2F"
                          dark
                          label
                          class="font-weight-bold px-4"
                        >
                          NO
                        </v-chip>

                        <div class="text-caption mt-3 grey--text text--darken-2">
                          (Utilizzabile nei giorni: 
                          <span 
                            v-for="(d, di) in dows.filter(d => settings.validDoW.includes(d.code))" 
                            :key="di" 
                            :class="localizedDoW == d.code ? 'font-weight-bold dark--text' : ''"
                          >
                            {{ d['it'].substring(0, 12).toUpperCase() }}{{ (di <= dows.filter(d => settings.validDoW.includes(d.code)).length - 2) ? ', ' : '' }}
                          </span>)
                        </div>
                      </div>
                    </div>

                    <!-- QR Code / Status Section -->
                    <div class="coupon-section text-center">
                      <!-- Active Coupon Code -->
                      <template v-if="!settings.spentDate && !settings.expired">
                        <div class="section-title text-subtitle-1 mb-3">
                          Mostra questo QR Code alla cassa
                        </div>
                        <div 
                          class="qr-wrapper d-flex justify-center pa-4 mx-auto cursor-pointer" 
                          @click="dialog_showPwd = !dialog_showPwd"
                        >
                          <qrcode-vue 
                            value="https://ibookrestaurant.com/validateqr/?p=verification" 
                            :size="200" 
                            render-as="svg" 
                            level="L" 
                            :margin="10"
                          ></qrcode-vue>
                        </div>
                        <div class="text-caption grey--text text--darken-1 mt-2">
                          Clicca sul QR per invalidare manualmente
                        </div>
                      </template>

                      <!-- Spent Coupon -->
                      <template v-else-if="settings.spentDate">
                        <div class="status-box status-spent pa-4">
                          <v-icon color="#D32F2F" large class="mb-2">mdi-check-circle-outline</v-icon>
                          <div class="text-h6 font-weight-bold red--text text--darken-2">
                            Coupon già speso il {{ settings.spentDate }}
                          </div>
                        </div>
                      </template>

                      <!-- Expired Coupon -->
                      <template v-else>
                        <div class="status-box status-expired pa-4">
                          <v-icon color="#D32F2F" large class="mb-2">mdi-clock-alert-outline</v-icon>
                          <div class="text-h6 font-weight-bold red--text text--darken-2">
                            Coupon scaduto il {{ settings.expiry }}
                          </div>
                        </div>
                      </template>
                    </div>

                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Scroll to Top Button -->
    <v-btn
      v-if="settings"
      v-scroll="onScroll"
      v-show="scrollToTop"
      fab
      dark
      small
      fixed
      bottom
      right
      color="#000000"
      @click="toTop"
    >
      <v-icon>keyboard_arrow_up</v-icon>
    </v-btn>

    <!-- Social Bottom Navigation -->
    <v-bottom-navigation
      color="orange"
      absolute
      hide-on-scroll
      scroll-target="#scroll-threshold-example"
      scroll-threshold="500"
      class="social-nav mt-6"
      style="position:unset"
      v-if="settings && settings.options"
    >
      <v-btn :href="settings.options.facebookURL" v-if="settings.options.facebookURL" text>
        <span>Facebook</span>
        <v-icon color="#3b5998">mdi-facebook</v-icon>
      </v-btn>
      <v-btn :href="settings.options.instagramURL" v-if="settings.options.instagramURL" text>
        <span>Instagram</span>
        <v-icon color="#C13584">mdi-instagram</v-icon>
      </v-btn>
      <v-btn :href="settings.options.youtubeURL" v-if="settings.options.youtubeURL" text>
        <span>YouTube</span>
        <v-icon color="#c4302b">mdi-youtube</v-icon>
      </v-btn>
      <v-btn :href="settings.options.linkedInURL" v-if="settings.options.linkedInURL" text>
        <span>LinkedIn</span>
        <v-icon color="#0077b5">mdi-linkedin</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <!-- Dialog: Confirm Invalidate -->
    <v-dialog v-model="dialog_showPwd" width="420">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          Spendi Coupon
          <v-spacer></v-spacer>
          <v-btn fab icon small @click="dialog_showPwd = false">
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-5 text-body-1 red--text text--darken-2 text-center">
          <v-icon color="warning" large class="mb-2">mdi-alert-outline</v-icon>
          <div>Attenzione! Cliccando sul pulsante sottostante il coupon verrà <strong>definitivamente invalidato</strong>.</div>
        </v-card-text>
        <v-card-text class="pb-5">
          <v-btn 
            color="#D32F2F" 
            dark 
            depressed 
            block 
            class="submit-btn" 
            @click="validateQR(1)" 
            :loading="loading"
          >
            Invalida Coupon
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Success (Coupon Spent) -->
    <v-dialog v-model="dialog_isValid" width="420" v-if="payloads">
      <v-card class="dialog-card text-center pa-4">
        <v-card-title class="dialog-title justify-center text-h5">
          Coupon Speso
        </v-card-title>
        <v-card-text class="mt-3">
          <v-icon color="#00AF87" style="font-size:100px">mdi-check-circle-outline</v-icon>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="#000000" dark depressed class="px-6" @click="dialog_isValid = false">
            Chiudi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Error -->
    <v-dialog v-model="dialog_isNotValid" width="420" v-if="payloads">
      <v-card class="dialog-card text-center pa-4">
        <v-card-title class="dialog-title justify-center text-h5">
          Errore
        </v-card-title>
        <v-card-text class="mt-2">
          <v-icon color="#D32F2F" style="font-size:100px">mdi-close-circle-outline</v-icon>
        </v-card-text>
        <v-card-text class="text-body-1 font-weight-medium grey--text text--darken-3">
          <div v-if="payloads.emailError">
            Email Errata
          </div>          
          <div v-else>
            Email errata, <br>
            Coupon scaduto o già speso.
          </div>        
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="#000000" dark depressed class="px-6" @click="dialog_isNotValid = false">
            Chiudi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>    

    <v-snackbar v-model="snackbar.active" top timeout="2000" :color="snackbar.color">
      {{ snackbar.text }}
    </v-snackbar>    
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import QrcodeVue from 'qrcode.vue';
import { required, minLength } from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";

const defaultForm = {
  password: "",
};

export default {
  mixins: [validationMixin],
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(6),
      },
    },
  },
  validationMessages: {
    form: {
      password: {
        required: "Please enter password",
        minLength: "Password must be of 6 characters",
      },
    },
  },
  components: {
    QrcodeVue,
  },
  data() {
    return {
      page: "Review",
      wHeight: window.innerHeight,
      wWidth: window.innerWidth,
      dialog_thanks: false,
      dialog_leave_to_tripadvisor: false,
      dialog_connection_error: false,
      dialog_duplicated: false,
      dialog_showPwd: false,
      dialog_isValid: false,
      dialog_isNotValid: false,
      loading: false,
      scrollToTop: false,
      form: Object.assign({}, defaultForm),
      origin: null,
      date: null,
      today: new Date().toISOString().substring(0, 10),
      locale: 'it',   
      snackbar: { active: false, color: 'success', text: '' },
      reviewSaved: false,
      uu: null,
      user: {},
      booking: { firstName: null, lastName: null },
      bookingId: null,
      totalScore: 0,
      coupon: null,
      partialScore: 0,
      review: { "food": 5, "service": 5, "value": 5, "location": 5, "review": null },
      settings: null,
      reviewPrizeValidUntil: null,
      QRCodeLink: '',
      payloads: null,
      hostname: null,
      dows: [
        { code: 1, it: 'Lunedì', en: 'Mondays' },
        { code: 2, it: 'Martedì', en: 'Tuesdays' },
        { code: 3, it: 'Mercoledì', en: 'Wednesdays' },
        { code: 4, it: 'Giovedì', en: 'Thursdays' },
        { code: 5, it: 'Venerdì', en: 'Fridays' },
        { code: 6, it: 'Sabato', en: 'Saturdays' },
        { code: 7, it: 'Domenica', en: 'Sundays' }
      ],      
    };
  },
  created() {
    this.$vuetify.theme.dark = false;
    sessionStorage.removeItem('jwt');
    const la = window.navigator.userLanguage || window.navigator.language;
    this.locale = la.substring(0, 2);
    this.$store.dispatch("setLocale", this.locale);
    this.couponId = this.$route.query.p;
    this.origin = this.$route.path;
    var hostname = window.location.hostname;
    this.QRCodeLink = 'https://' + hostname + '/qrcodevalidate/?p=' + this.couponId;
    this.Authenticate(this.couponId);
  },  
  computed: {
    ...mapGetters(["countries"]),
    localizedDoW: function() {
      var z = new Date().getDay();
      return z === 0 ? 7 : z;
    }    
  },
  methods: {
    async validateQR(v) {
      this.loading = true;
      this.form.couponId = this.couponId;
      this.form.validate = v;
      let payload = { "action": 'validateQR', "payload": this.form };
      let w = await this.updateDB(payload);
      this.loading = false;
      this.dialog_showPwd = false;
      this.payloads = w;
      if (w && w.errorid == '0') {
        this.dialog_isValid = true;
      } else {
        this.dialog_isNotValid = true;
      }
    },    
    onScroll(e) {
      if (typeof window === 'undefined') return;
      const top = window.pageYOffset || e.target.scrollTop || 0;
      this.scrollToTop = top > 20;
    },
    toTop() {
      this.$vuetify.goTo(0);
    },
    async Authenticate(couponId) {
      this.loading = true;
      try {
        const payload = JSON.stringify({ "action": "AuthenticateCoupon", "payload": { "couponId": couponId } });
        const res = await this.$http.post('/review/review.php', payload);
        switch (res.data.errorid) {
          case '0':
            sessionStorage.setItem('jwt', res.data.jwt);
            this.settings = res.data.payloads;
            if (this.settings.options && this.settings.options.brand) {
              document.title = this.settings.options.brand + ' | Coupon Omaggio';
            }
            break;
          case '0006':
            this.$router.push('/auth/login').catch(() => {});
            break;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async updateDB(payload) {
      try {
        const res = await this.$http.post('/review/review.php', payload);
        if (res.data.errorid == '0') {
          sessionStorage.setItem('jwt', res.data.jwt);
        }
        return res.data;
      } catch (e) {
        this.dialog_connection_error = true;
        console.error(e);
      }
    },    
  },
};
</script>

<style scoped>
/* Main Page Layout */
.coupon-page {
  background: #FFFFFF;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #16211C;
}

/* Brand Card */
.brand-card {
  background: #FFFFFF !important;
  border: 1px solid #E3E7E4;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.brand-card-inner {
  display: flex;
  align-items: center;
}

.brand-logo {
  border-radius: 6px;
}

.brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #0B3B24;
}

/* Greeting Block */
.greeting-block {
  margin: 6px 0;
}

.guest-name {
  font-weight: 700;
  color: #0B3B24;
}

/* Coupon Main Container & Sections */
.coupon-card {
  background: #FFFFFF !important;
  border: 1px solid #E3E7E4;
  border-radius: 8px !important;
  padding: 12px;
}

.coupon-section {
  background: #FFFFFF;
  border: 1px solid #E3E7E4;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.section-title {
  font-weight: 700;
  color: #0B3B24;
}

.emerald-text {
  color: #00AF87;
}

.cursor-pointer {
  cursor: pointer;
}

/* QR Code Wrapper */
.qr-wrapper {
  background: #FFFFFF;
  border: 1px dashed #B9C2BC;
  border-radius: 8px;
  width: fit-content;
  transition: border-color 0.2s ease;
}

.qr-wrapper:hover {
  border-color: #00AF87;
}

/* Status Cards */
.status-box {
  border-radius: 6px;
  background: #FFEBEE;
  border: 1px solid #FFCDD2;
}

/* Bottom Navigation */
.social-nav {
  background: transparent !important;
  border-top: 1px solid #E3E7E4;
  box-shadow: none !important;
}

.social-nav ::v-deep .v-btn {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 1px;
  color: #4A5750;
}

/* Dialog Styles */
.dialog-card {
  border-radius: 10px !important;
  overflow: hidden;
}

.dialog-title {
  font-weight: 700;
  color: #0B3B24;
}

.submit-btn {
  border-radius: 6px !important;
  font-weight: 700;
  font-size: 15px;
  height: 44px !important;
  text-transform: none;
}

/* Responsive Tweaks */
@media (max-width: 600px) {
  .coupon-section {
    padding: 16px;
    margin-top: 12px;
  }
}
</style>